import React from 'react';
import { Box, Typography } from '@mui/material';
import ZoomableImage from './ZoomableImage';
import type { ReceiptClientMetadata } from '../../types/services/audit';

interface FixOcrImagePanelProps {
  imageUrl: string | null;
  loading: boolean;
  saving: boolean;
  ticketCount: number;
  reportType?: string;
  onEditReceipt: () => void;
  hoveredRowIndex?: number | null;
  onRowHover?: (index: number | null) => void;
  onGuideLineClientYChange?: (clientY: number | null) => void;
  showHoverGuideLine?: boolean;
  showGuideGrid?: boolean;
  guideGridOriginClientY?: number | null;
  clientMetadata?: ReceiptClientMetadata | null;
}

const REPORT_TOP_MARGIN_BY_TYPE: Record<string, string> = {
  vending_inventory: '218px',
  vending_sales: '260px',
  vending_shift_report: '248px',
};

const formatMetadataValue = (value: string | null | undefined): string | null => {
  const normalizedValue = value?.trim();
  return normalizedValue || null;
};

const formatPlatform = (platform: string | null): string | null => {
  if (platform === 'android') return 'Android';
  if (platform === 'ios') return 'iOS';
  return platform;
};

const UploadMetadata: React.FC<{ metadata: ReceiptClientMetadata }> = ({ metadata }) => {
  const deviceModel = formatMetadataValue(metadata.device_model);
  const platform = formatPlatform(formatMetadataValue(metadata.platform));
  const osVersion = formatMetadataValue(metadata.os_version);
  const appVersion = formatMetadataValue(metadata.native_app_version);
  const buildNumber = formatMetadataValue(metadata.native_build_number);
  const easRuntimeVersion = formatMetadataValue(metadata.eas?.runtime_version);

  const deviceDetails = [
    deviceModel,
    platform ? `${platform}${osVersion ? ` ${osVersion}` : ''}` : osVersion,
    appVersion ? `${appVersion}${buildNumber ? `(${buildNumber})` : ''}` : buildNumber,
    `EAS (${easRuntimeVersion || '-'})`,
  ].filter((value): value is string => Boolean(value));

  if (deviceDetails.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mb: 0.75,
        px: 1.25,
        py: 0.75,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="body2">
        <Box component="span" sx={{ fontWeight: 700 }}>Device:</Box>{' '}
        {deviceDetails.join(' - ')}
      </Typography>
    </Box>
  );
};

const FixOcrImagePanel: React.FC<FixOcrImagePanelProps> = ({
  imageUrl,
  loading,
  saving,
  ticketCount,
  reportType,
  onEditReceipt,
  hoveredRowIndex,
  onRowHover,
  onGuideLineClientYChange,
  showHoverGuideLine,
  showGuideGrid,
  guideGridOriginClientY,
  clientMetadata,
}) => {
  const topMargin = REPORT_TOP_MARGIN_BY_TYPE[reportType || ''] || '120px';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: `calc(100% - ${topMargin})`, minHeight: 520, marginTop: topMargin }}>
      {clientMetadata && <UploadMetadata metadata={clientMetadata} />}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'stretch', justifyContent: 'center', position: 'relative' }}>
        {imageUrl ? (
          <ZoomableImage
            imageUrl={imageUrl}
            onClose={() => {}}
            hoveredRowIndex={hoveredRowIndex}
            onRowHover={onRowHover}
            onGuideLineClientYChange={onGuideLineClientYChange}
            showHoverGuideLine={showHoverGuideLine}
            showGuideGrid={showGuideGrid}
            guideGridOriginClientY={guideGridOriginClientY}
            ticketCount={ticketCount}
            onEditReceipt={onEditReceipt}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No image available
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default FixOcrImagePanel;
